import { useEffect, useState } from 'react'
import { AlertTriangle, FileText, RefreshCw } from 'lucide-react'
import { listarErrosSistema } from '../services/errosSistemaService'
import { ErroSistemaArquivo } from '../tipos/errosSistema'
import { useToast } from '../../../contexts/NotificationContext'

function ErrosSistema() {
  const [arquivos, setArquivos] = useState<ErroSistemaArquivo[]>([])
  const [arquivoSelecionado, setArquivoSelecionado] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [limitePorArquivo, setLimitePorArquivo] = useState(100)
  const toast = useToast()

  const carregar = () => {
    setIsLoading(true)
    listarErrosSistema(limitePorArquivo)
      .then((data) => {
        setArquivos(data)
        if (data.length > 0 && !arquivoSelecionado)
          setArquivoSelecionado(data[0].nomeArquivo)
      })
      .catch((error: any) => {
        const mensagem = error?.message || 'Erro ao carregar erros de sistema'
        toast.error('Erro', mensagem)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  useEffect(() => {
    carregar()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const arquivosOrdenados = [...arquivos].sort((a, b) => b.data.localeCompare(a.data))
  const atual = arquivosOrdenados.find((a) => a.nomeArquivo === arquivoSelecionado) || arquivosOrdenados[0] || null

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Erros de Sistema</h1>
          <p className="mt-1 text-sm text-gray-600">
            Erros não tratados capturados pelo middleware central, agrupados por arquivo de log diário.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex items-center space-x-3">
          <div className="flex items-center space-x-2 text-xs text-gray-600">
            <span>Erros por dia:</span>
            <select
              value={limitePorArquivo}
              onChange={(e) => setLimitePorArquivo(Number(e.target.value))}
              className="border border-gray-300 rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-brand-500"
            >
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
              <option value={0}>Todos</option>
            </select>
          </div>
          <button
            type="button"
            onClick={carregar}
            disabled={isLoading}
            className="inline-flex items-center px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 transition-all duration-200 shadow-theme-sm font-medium disabled:opacity-70"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Atualizar
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-theme-sm overflow-hidden">
        {isLoading ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-500 mx-auto" />
            <p className="mt-3 text-gray-600">Carregando erros de sistema...</p>
          </div>
        ) : arquivosOrdenados.length === 0 ? (
          <div className="p-12 text-center">
            <FileText className="w-14 h-14 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600">Nenhum erro de sistema encontrado.</p>
            <p className="mt-1 text-sm text-gray-500">
              Quando ocorrerem erros não tratados no backend, eles aparecerão aqui.
            </p>
          </div>
        ) : (
          <div className="flex flex-col md:flex-row">
            <div className="md:w-64 border-b md:border-b-0 md:border-r border-gray-200 bg-gray-50">
              <div className="px-4 py-3 border-b border-gray-200 text-xs font-semibold text-gray-700 flex items-center space-x-2">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                <span>Arquivos de log</span>
              </div>
              <div className="divide-y divide-gray-200 max-h-80 overflow-y-auto">
                {arquivosOrdenados.map((arquivo) => (
                  <button
                    key={arquivo.nomeArquivo}
                    type="button"
                    onClick={() => setArquivoSelecionado(arquivo.nomeArquivo)}
                    className={`w-full text-left px-4 py-2 text-xs ${
                      arquivo.nomeArquivo === arquivoSelecionado
                        ? 'bg-white text-brand-600 font-semibold'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span>{arquivo.data}</span>
                      <span className="text-[10px] text-gray-500 ml-2">{arquivo.erros.length} erro(s)</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
            <div className="flex-1">
              <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-900">
                    {atual ? `Erros do dia ${atual.data}` : 'Selecione um arquivo de log'}
                  </p>
                  <p className="text-xs text-gray-500">
                    Conteúdo bruto gravado pelo middleware de tratamento de erros.
                  </p>
                </div>
              </div>
              <div className="p-4 max-h-[480px] overflow-auto text-xs font-mono bg-gray-50">
                {!atual ? (
                  <p className="text-gray-500">Selecione um arquivo de log na lateral para visualizar.</p>
                ) : (
                  atual.erros.map((erro, index) => (
                    <div key={`${atual.nomeArquivo}-${index}`} className="mb-4">
                      <pre className="whitespace-pre-wrap break-words text-gray-800">{erro}</pre>
                      {index < atual.erros.length - 1 && (
                        <div className="border-b border-dashed border-gray-300 my-2" />
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default ErrosSistema

